import { useEffect, useRef, useState } from 'react';
import { Modal, Button, SegmentedControl } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import React from 'react';
import PointsEditor from './options/pointsEditor';
import ContextMenu from './options/contextMenu';
import ZoomControls from './options/zoomControls';

type Mode = 'insert' | 'move' | 'moveSVG';

interface Props {
    points: string;
    polylineStyles: any;
    onPointsChange: (newPoints: string) => void;
}

export const PolylineEditor: React.FC<Props> = ({ points, polylineStyles, onPointsChange }) => {
    const { t } = useTranslation();
    const svgRef = useRef<SVGSVGElement>(null);
    const [opened, setOpened] = useState(false);
    const [currentPoints, setCurrentPoints] = useState(points);
    const pointArray = currentPoints.split(' ').map(point => point.split(','));
    const [cursor, setCursor] = useState('crosshair');

    const [history, setHistory] = useState<string[]>([]);
    const [redoStack, setRedoStack] = useState<string[]>([]);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });

    const [mode, setMode] = useState<Mode>('insert');
    const [dragging, setDragging] = useState<boolean>(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const [scale, setScale] = useState<number>(1);
    const [isCtrlPressed, setIsCtrlPressed] = useState(false);
    const [draggingCanvas, setDraggingCanvas] = useState(false);
    const [startDrag, setStartDrag] = useState<{ x: number; y: number } | null>(null);

    const SVG_WIDTH = 1030;
    const SVG_HEIGHT = 750;
    const PADDING = 5;

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === ' ' || e.ctrlKey) {
                setIsCtrlPressed(true);
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === ' ' || e.ctrlKey) {
                setIsCtrlPressed(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    useEffect(() => {
        if (points) {
            setCurrentPoints(points);
        } else {
            setCurrentPoints("30,30");
        }
    }, [points]);

    const closeContextMenu = () => {
        setContextMenu(prev => ({ ...prev, visible: false }));
    };

    const handleCircleClick = (index: number) => {
        if (mode === 'move') {
            setActiveIndex(index);
        }
    };

    const resetActiveIndex = () => {
        setActiveIndex(null);
    };

    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        if (mode === 'move' && dragging && activeIndex !== null && svgRef.current) {
            const svg = svgRef.current;
            const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(svg.getScreenCTM()?.inverse());

            setCurrentPoints(prev => {
                const pointsArray = prev.split(' ').map(p => p.split(','));
                pointsArray[activeIndex] = [`${Math.floor(pt.x)}`, `${Math.floor(pt.y)}`];

                const updatedPoints = pointsArray.map(p => p.join(',')).join(' ');
                onPointsChange(updatedPoints);
                return updatedPoints;
            });
        }

        if (mode === 'moveSVG' && draggingCanvas && svgRef.current) {
            const dx = e.clientX - startDrag!.x;
            const dy = e.clientY - startDrag!.y;

            const svg = svgRef.current;
            const viewBox = svg.viewBox.baseVal;
            svg.setAttribute('viewBox', `${viewBox.x - dx} ${viewBox.y - dy} ${viewBox.width} ${viewBox.height}`);

            setStartDrag({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
        setIsCtrlPressed(false);
        if (dragging) {
            setDragging(false);
            setHistory(prev => [...prev, currentPoints]);
        }
        if (draggingCanvas) {
            setDraggingCanvas(false);
            setStartDrag(null);
        }
    };

    const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
        if (e.button === 0) {
            if (mode === 'move' && activeIndex !== null) {
                setDragging(true);
            }

            if (mode === 'moveSVG') {
                setDraggingCanvas(true);
                setStartDrag({ x: e.clientX, y: e.clientY });
                resetActiveIndex();
            }
        }
    };

    const handlePointClick = (e: React.MouseEvent<SVGSVGElement>) => {
        if (contextMenu.visible) {
            closeContextMenu();
        }
        if (mode !== 'insert' || !svgRef.current || (isCtrlPressed || draggingCanvas)) return;

        const svg = svgRef.current;
        const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(svg.getScreenCTM()?.inverse());

        if (pt.x < PADDING || pt.x > SVG_WIDTH - PADDING || pt.y < PADDING || pt.y > SVG_HEIGHT - PADDING) {
            return;
        }

        setHistory(prev => [...prev, currentPoints]);
        setRedoStack([]);

        setCurrentPoints(prevPoints => {
            const newPoint = `${Math.floor(pt.x)},${Math.floor(pt.y)}`;
            const updatedPoints = prevPoints ? `${prevPoints} ${newPoint}` : newPoint;
            onPointsChange(updatedPoints);
            return updatedPoints;
        });
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        setContextMenu(
            {
                x: e.clientX - 565,
                y: e.clientY - 54,
                visible: true
            }
        );
    };

    const handleModeChange = (val: Mode) => {
        resetActiveIndex();
        setMode(val);

        switch (val) {
            case 'insert':
                setCursor('crosshair');
                break;
            case 'move':
                setCursor('default');
                break;
            case 'moveSVG':
                setCursor('grab');
                break;
            default:
                break;
        }
    };

    return (
        <>
            <Button onClick={() => setOpened(true)} w="100%" radius={0}>
                {t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.editPolilyne')}
            </Button>

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title={t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.title')}
                size="xl"
                aria-hidden={opened ? 'false' : 'true'}
            >
                <SegmentedControl
                    w={"100%"}
                    radius={0}
                    value={mode}
                    onChange={(val) => handleModeChange(val as Mode)}
                    data={[
                        {
                            label: t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.mods.insert'),
                            value: 'insert'
                        },
                        {
                            label: t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.mods.move'),
                            value: 'move'
                        },
                        {
                            label: t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.mods.moveSVG'),
                            value: 'moveSVG'
                        }
                    ]}
                />

                <div style={{ position: 'relative' }}>
                    <ZoomControls setScale={setScale} />

                    <svg
                        ref={svgRef}
                        viewBox={`0 0 ${SVG_WIDTH / scale} ${SVG_HEIGHT / scale}`}
                        width="100%"
                        height="100%"
                        style={{
                            border: '1px solid black',
                            cursor: cursor,
                            backgroundSize: '35px 35px',
                            backgroundImage: `linear-gradient(to right, rgba(128, 128, 128, 0.2) 1px, transparent 1px),
                                          linear-gradient(to bottom, rgba(128, 128, 128, 0.2) 1px, transparent 1px)`,
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                        }}
                        onClick={handlePointClick}
                        onContextMenu={handleContextMenu}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseDown={handleMouseDown}
                    >
                        <polyline points={currentPoints} style={polylineStyles} />

                        {pointArray.length > 0 && pointArray.map(([x, y], index) => {
                            const parsedX = parseFloat(x);
                            const parsedY = parseFloat(y);

                            return !isNaN(parsedX) && !isNaN(parsedY) ? (
                                <React.Fragment key={`point-${index}`}>
                                    <circle
                                        cx={parsedX}
                                        cy={parsedY}
                                        r={4}
                                        fill={index === activeIndex && mode === 'move' ? 'green' : 'red'}
                                        onClick={() => handleCircleClick(index)}
                                        style={activeIndex === null && mode === 'move' ? { cursor: 'pointer' } : {}}
                                    />
                                    <text
                                        x={parsedX - 5}
                                        y={parsedY - 12}
                                        fontSize="16px"
                                        strokeWidth="2"
                                        fill={index === activeIndex && mode === 'move' ? 'green' : 'red'}
                                        stroke={index === activeIndex && mode === 'move' ? 'green' : 'red'}
                                    >
                                        {index + 1}
                                    </text>
                                </React.Fragment>
                            ) : null;
                        })}
                    </svg>
                </div>

                {pointArray.length > 0 && (
                    <PointsEditor
                        t={t}
                        pointArray={pointArray}
                        currentPoints={currentPoints}
                        setHistory={setHistory}
                        setCurrentPoints={setCurrentPoints}
                        onPointsChange={onPointsChange}
                    />
                )}

                {contextMenu.visible && (
                    <ContextMenu
                        t={t}
                        contextMenu={contextMenu}
                        activeIndex={activeIndex}
                        history={history}
                        redoStack={redoStack}
                        currentPoints={currentPoints}
                        closeContextMenu={closeContextMenu}
                        resetActiveIndex={resetActiveIndex}
                        onPointsChange={onPointsChange}
                        setHistory={setHistory}
                        setRedoStack={setRedoStack}
                        setCurrentPoints={setCurrentPoints}
                    />
                )}
            </Modal>
        </>
    );
};

export default PolylineEditor;
